import React, { useEffect, useState } from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import { CloseOutlined, DownOutlined, PhoneFilled, PhoneOutlined, UpOutlined } from '@ant-design/icons';
import { history, useAccess, useLocation } from '@umijs/max';
import { useCallbackQueue } from '@/contexts/CallbackQueueContext';
import { callbackSourceTag } from '@/services/app/tenant/call-queue';
import CallbackQueueItem, { fmtCallbackDate } from '@/components/CallbackQueue/CallbackQueueItem';
import { useCallbackActions } from '@/components/CallbackQueue/useCallbackActions';
import { LS_PREFIX } from '@/constants';
import { parseDt } from '@/util';

const CALL_BOARD_PATH = '/tenant/communications/call_board';
const LS_EXPANDED_KEY = `${LS_PREFIX}callback_banner_expanded`;
const WAIT_TICK_MS = 30_000;

/**
 * "12m" / "3h 05m" / "2d 4h" since `serverDt` — how long the oldest callback has been waiting.
 * `created_at` is a naive 'Y-m-d H:i:s' string in the SERVER timezone (PHP default,
 * America/Phoenix), so it must go through parseDt — a raw `new Date(str)` reads it as
 * browser-local time and an Eastern user sees every new callback as already "3h" old.
 */
const fmtWaiting = (serverDt: string, now: number) => {
  const mins = Math.max(0, Math.floor((now - parseDt(serverDt).valueOf()) / 60_000));
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${String(mins % 60).padStart(2, '0')}m`;
  return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
};

/**
 * Site-wide banner shown while callbacks are pending (client request: keep it visible
 * on every page until a CSR calls the customer back or abandons the callback). Reads the
 * same shared CallbackQueueContext as the nav badge — exact pending count plus the oldest
 * waiting rows — so it costs no extra requests and updates on the same WS push.
 *
 * ONE banner for all callbacks, never one per callback: the queue is tenant-wide and can
 * run long, so a stacked banner per row would push the page off screen. The collapsed row
 * always shows the oldest callback inline (FIFO for CSRs) with its actions; "Show all"
 * expands the preview list in place. There is deliberately no close/snooze — the banner
 * only goes away when the queue is empty. Expand/collapse is the only per-user state.
 *
 * Hidden on the Call Board page (the full panel lives there) and for users without call
 * board access, mirroring the nav badge. Mounted inside the shared sticky wrapper in
 * app.tsx together with AiRescheduleBanner.
 */
const CallbackQueueBanner: React.FC = () => {
  const access = useAccess();
  const location = useLocation();
  const { queue: allQueue, pendingCount } = useCallbackQueue();

  const [expanded, setExpanded] = useState<boolean>(() => localStorage.getItem(LS_EXPANDED_KEY) === '1');
  const [now, setNow] = useState(() => Date.now());
  const { updatingId, callBack, dismiss } = useCallbackActions();

  // Re-render the "waiting Xm" label without any data fetch.
  useEffect(() => {
    if (pendingCount < 1) return undefined;
    const t = setInterval(() => setNow(Date.now()), WAIT_TICK_MS);
    return () => clearInterval(t);
  }, [pendingCount]);

  const toggleExpanded = () => {
    setExpanded((v) => {
      localStorage.setItem(LS_EXPANDED_KEY, v ? '0' : '1');
      return !v;
    });
  };

  const queue = allQueue
    .filter((i) => i.status === 'pending')
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const oldest = queue[0];

  const onCallBoard = location.pathname.includes(CALL_BOARD_PATH);
  if (!access?.canCallBoardMenu || onCallBoard || pendingCount < 1 || !oldest) return null;

  const source = callbackSourceTag(oldest.source);
  const busy = updatingId === oldest.id;
  const locked = updatingId !== null && !busy;
  const showToggle = pendingCount > 1;

  return (
    <div
      role="status"
      style={{
        background: '#fff1f0',
        borderBottom: '1px solid #ffccc7',
        padding: '6px 16px',
        fontSize: 13,
        lineHeight: '22px',
      }}
    >
      {/* collapsed row — always visible */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Typography.Text strong style={{ color: '#cf1322', whiteSpace: 'nowrap' }}>
          <PhoneFilled style={{ marginRight: 6 }} />
          {pendingCount} callback{pendingCount === 1 ? '' : 's'} waiting
        </Typography.Text>

        <span
          style={{
            color: '#595959',
            flex: '1 1 auto',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{pendingCount === 1 ? 'Waiting:' : 'Oldest:'}</span>
          <Typography.Text strong style={{ fontSize: 13 }}>
            {oldest.customer_name ? `${oldest.customer_name} · ` : ''}
            {oldest.customer_phone}
          </Typography.Text>
          <span style={{ whiteSpace: 'nowrap' }}>· waiting {fmtWaiting(oldest.created_at, now)}</span>
          {oldest.reason && (
            <Typography.Text type="secondary" ellipsis style={{ maxWidth: 280, fontSize: 12 }} title={oldest.reason}>
              · {oldest.reason}
            </Typography.Text>
          )}
          {source && (
            <Tag color={source.color} style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>
              {source.label}
            </Tag>
          )}
          {oldest.contacted_at && (
            <Tag color="green" icon={<PhoneOutlined />} style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>
              Already contacted · {fmtCallbackDate(oldest.contacted_at)}
            </Tag>
          )}
        </span>

        <Space size={4} style={{ marginLeft: 'auto' }}>
          <Button
            type="primary"
            size="small"
            icon={<PhoneOutlined />}
            loading={busy}
            disabled={locked}
            onClick={() => callBack(oldest)}
          >
            Call Back
          </Button>
          <Button size="small" icon={<CloseOutlined />} loading={busy} disabled={locked} onClick={() => dismiss(oldest)}>
            Dismiss
          </Button>
          {showToggle && (
            <Button
              type="link"
              size="small"
              icon={expanded ? <UpOutlined /> : <DownOutlined />}
              onClick={toggleExpanded}
              style={{ padding: '0 4px' }}
            >
              {expanded ? 'Hide' : `Show all ${pendingCount}`}
            </Button>
          )}
          <Button type="link" size="small" style={{ padding: '0 4px' }} onClick={() => history.push(CALL_BOARD_PATH)}>
            Call Board
          </Button>
        </Space>
      </div>

      {/* expanded preview list — same rows as the nav badge popover */}
      {expanded && showToggle && (
        <div
          style={{
            marginTop: 6,
            background: '#fff',
            border: '1px solid #ffccc7',
            borderRadius: 4,
            padding: '0 12px',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {queue.map((item) => (
            <CallbackQueueItem
              key={item.id}
              item={item}
              updatingId={updatingId}
              onCallBack={callBack}
              onDismiss={dismiss}
            />
          ))}
          {pendingCount > queue.length && (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 11, display: 'block', textAlign: 'center', padding: '8px 0' }}
            >
              Showing {queue.length} of {pendingCount} — see all in Call Board
            </Typography.Text>
          )}
        </div>
      )}
    </div>
  );
};

export default CallbackQueueBanner;
